import React from "react";
import { motion } from "framer-motion";
import { Compass, FlaskConical, Cpu, Rocket, Workflow, ArrowRight } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const steps = [
  {
    id: "01",
    badge: "DISCOVERY",
    title: "Problem Mapping",
    timeline: "1–2 Weeks",
    deliverable: "AI Strategy",
    description:
      "We analyze your business, existing systems and datasets, then define a roadmap that maximizes AI ROI while minimizing implementation risk.",
    color: "cyan",
    icon: Compass,
  },
  {
    id: "02",
    badge: "PROTOTYPE",
    title: "Rapid Prototype",
    timeline: "2–3 Weeks",
    deliverable: "Working PoC",
    description:
      "A proof of concept is developed using real data so stakeholders can validate functionality before production engineering begins.",
    color: "purple",
    icon: FlaskConical,
  },
  {
    id: "03",
    badge: "BUILD",
    title: "Production Engineering",
    timeline: "4–8 Weeks",
    deliverable: "Production AI",
    description:
      "We build scalable APIs, model serving, CI/CD pipelines, monitoring, security and cloud infrastructure for enterprise deployment.",
    color: "cyan",
    icon: Cpu,
  },
  {
    id: "04",
    badge: "DEPLOY",
    title: "Launch & Support",
    timeline: "Ongoing",
    deliverable: "Post-Launch Support",
    description:
      "Deployment, monitoring, documentation and continuous support ensure long-term success after launch.",
    color: "purple",
    icon: Rocket,
  },
];

const colorMap = {
  cyan: {
    text: "text-cyan-300",
    border: "border-cyan-400/30",
    iconBg: "from-cyan-400/20 to-cyan-500/5",
    glow: "shadow-[0_0_30px_-8px_rgba(34,211,238,0.4)]",
    dot: "bg-cyan-300",
  },
  purple: {
    text: "text-purple-300",
    border: "border-purple-400/30",
    iconBg: "from-purple-400/20 to-purple-500/5",
    glow: "shadow-[0_0_30px_-8px_rgba(192,132,252,0.4)]",
    dot: "bg-purple-300",
  },
};

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

function ProcessHero() {
  return (
    <div className="mx-auto max-w-4xl text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-5 py-2 backdrop-blur-sm">
        <Workflow className="h-4 w-4 text-white/60" strokeWidth={1.75} />
        <span className="text-xs font-semibold uppercase tracking-[3px] text-white/60">
          Proven 4-Step Process
        </span>
      </div>

      <h2 className="mt-7 text-5xl md:text-6xl lg:text-7xl font-black leading-[1.08] tracking-tight">
        <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-400 bg-clip-text text-transparent">
          From Idea To Live AI
        </span>
        <br />
        <span className="text-white">In Weeks, Not Months</span>
      </h2>

      <p className="mx-auto mt-6 max-w-2xl text-lg text-white/45">
        A focused four-step system that takes you from a rough idea to a
        production AI product — no bloat, no guesswork, no surprises.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step card                                                           */
/* ------------------------------------------------------------------ */

function StepCard({ step, index }) {
  const c = colorMap[step.color];
  const Icon = step.icon;

  return (
    <motion.div
      className="relative flex flex-col"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay: index * 0.12 }}
    >
      {/* icon + number, sits on the connecting line */}
      <div className="relative flex items-center justify-center lg:justify-start">
        <div
          className={`relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl border ${c.border} bg-[#0B1220] bg-gradient-to-br ${c.iconBg} backdrop-blur-md ${c.glow}`}
        >
          <Icon className={`h-6 w-6 ${c.text}`} strokeWidth={1.5} />
        </div>
        <span
          className={`absolute -top-2 left-1/2 -translate-x-1/2 lg:left-12 lg:translate-x-0 rounded-full border ${c.border} bg-[#0B1220] px-2 py-0.5 text-[10px] font-bold ${c.text}`}
        >
          {step.id}
        </span>
      </div>

      <div className="mt-6 text-center lg:text-left">
        <span className={`text-[11px] font-semibold tracking-[3px] ${c.text}`}>
          {step.badge}
        </span>
        <h3 className="mt-2 text-xl font-bold text-white">{step.title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-white/50">
          {step.description}
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-white/40 lg:justify-start">
          <span className="flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
            {step.timeline}
          </span>
          <span className="h-3 w-px bg-white/15" />
          <span>{step.deliverable}</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Connecting line (desktop only)                                      */
/* ------------------------------------------------------------------ */

function ConnectingLine() {
  return (
    <div className="pointer-events-none absolute left-0 right-0 top-8 hidden lg:block">
      <div className="relative mx-auto max-w-7xl px-6">
        <div
          className="absolute top-0 h-px w-full bg-white/10"
          style={{ left: 0 }}
        />
        <motion.div
          className="absolute top-0 h-px bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400"
          style={{ left: 0 }}
          initial={{ width: "0%" }}
          whileInView={{ width: "100%" }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.4, ease: "easeInOut", delay: 0.2 }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

export default function ProcessSection() {
  return (
    <section
      className="relative overflow-hidden py-20 lg:py-24"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 0%, #16213F 0%, #0B1220 55%, #080D18 100%)",
      }}
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, black 40%, transparent 90%)",
          }}
        />
        <div className="absolute left-1/2 top-[-10%] h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-blue-500/[0.10] blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <ProcessHero />

        <div className="relative mt-20 lg:mt-24">
          <ConnectingLine />
          <div className="grid grid-cols-1 gap-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {steps.map((step, i) => (
              <StepCard key={step.id} step={step} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}